import { BaseQueryFn } from '@reduxjs/toolkit/query/react'
import { transformScalesReading, ScalesReadingError } from './utils'
import { type TransformedScalesReading } from './dto/ScalesReadingDto'
import { createEntityAdapter, type EntityState } from '@reduxjs/toolkit'
import { microserviceApi } from './MicroserviceBaseApi'
import { QueryCacheLifecycleApi } from '@reduxjs/toolkit/dist/query/endpointDefinitions'
import { WsEvent, WsMessageEvent } from '../../../utils/ws/WsHandler'
import { getScalesReadingWsHandler } from '../../../utils/globals/scalesRedingWsHandler'
import { scalesReadingReceived } from '../../store/scalesReading/actions'
import { MAX_SCALES_READINGS } from '../../../utils/constants'

const scalesReadingAdapter = createEntityAdapter({
  selectId: (model: TransformedScalesReading) => model.createdAt,
  sortComparer: (a, b) => a.createdAt - b.createdAt,
})

type EndpointApi = QueryCacheLifecycleApi<
  void,
  BaseQueryFn,
  EntityState<TransformedScalesReading>,
  'api'
>

function makeWsHandlerMessageListener<T extends EndpointApi = EndpointApi>(
  endpointApi: T,
) {
  const listener = async (event: WsEvent) => {
    const data = JSON.parse((event as WsMessageEvent).data as string)
    try {
      const transformedData = transformScalesReading(data)
      endpointApi.updateCachedData((draft) => {
        if (draft.ids.length === MAX_SCALES_READINGS) {
          const toRemove = draft.ids[0]
          scalesReadingAdapter.removeOne(draft, toRemove)
        }
        scalesReadingAdapter.addOne(draft, transformedData)
      })
      endpointApi.dispatch(scalesReadingReceived(transformedData))
    } catch (e) {
      if (e instanceof ScalesReadingError) {
        console.error(`Microservice API: scales reading error: ${e}`)
        return
      }
      throw e
    }
  }
  return listener
}

export const api = microserviceApi.injectEndpoints({
  endpoints: (builder) => ({
    getScalesReadings: builder.query<
      EntityState<TransformedScalesReading>,
      void
    >({
      queryFn: async () => {
        return { data: { ids: [], entities: {} } }
      },
      async onCacheEntryAdded(arg, endpointApi) {
        const listener = makeWsHandlerMessageListener(endpointApi)
        const wsHandler = getScalesReadingWsHandler()
        try {
          await endpointApi.cacheDataLoaded
          await wsHandler.connect()
          wsHandler.addEventListener('message', listener)
        } catch {}
        await endpointApi.cacheEntryRemoved
        wsHandler.removeEventListener('message', listener)
      },
    }),
  }),
})

export const { useGetScalesReadingsQuery, useLazyGetScalesReadingsQuery } = api
export const { selectEntities } = scalesReadingAdapter.getSelectors()
